import React, { useEffect, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Skeleton from '@mui/material/Skeleton';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Checkbox from '@mui/material/Checkbox';
import ListItemText from '@mui/material/ListItemText';
import { useTheme } from '@mui/material/styles';
import PersonOutlinedIcon from '@mui/icons-material/PersonOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import BlockOutlinedIcon from '@mui/icons-material/BlockOutlined';
import ToggleOnOutlinedIcon from '@mui/icons-material/ToggleOnOutlined';
import AttachFileOutlinedIcon from '@mui/icons-material/AttachFileOutlined';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import SwapVertOutlinedIcon from '@mui/icons-material/SwapVertOutlined';
import { GET } from '../../../../configs/api';
import { api } from '../../../../utils/constants';
import { formatTechnicianLogEntry, formatShortDateTime } from '../../../../utils/technicianLogFormatters';

const ACTIVITY_TYPE_CONFIG = {
  create: { label: 'Created', icon: PersonOutlinedIcon, color: 'success' },
  update: { label: 'Updated', icon: EditOutlinedIcon, color: 'primary' },
  blacklist: { label: 'Blacklist', icon: BlockOutlinedIcon, color: 'error' },
  active: { label: 'Active', icon: ToggleOnOutlinedIcon, color: 'warning' },
  attachment: { label: 'Documents', icon: AttachFileOutlinedIcon, color: 'info' },
  other: { label: 'Other', icon: HistoryOutlinedIcon, color: 'default' },
};

const getActivityType = (log) => {
  if (!log?.action || typeof log.action !== 'string') return 'other';
  if (log.action === 'technician.created') return 'create';
  if (log.action === 'technician.updated') return 'update';
  if (log.action === 'technician.blacklisted_updated') return 'blacklist';
  if (log.action === 'technician.active_updated') return 'active';
  if (log.action.startsWith('technician.attachment_')) return 'attachment';
  return 'other';
};

const toStartOfDay = (value) => {
  if (!value) return null;
  const date = new Date(`${value}T00:00:00`);
  return Number.isNaN(date.getTime()) ? null : date.getTime();
};

const toEndOfDay = (value) => {
  if (!value) return null;
  const date = new Date(`${value}T23:59:59.999`);
  return Number.isNaN(date.getTime()) ? null : date.getTime();
};

const getActorFilterValue = (log) => log.actor_user_id || log.actor_email || 'system';
const getActorFilterLabel = (log) => log.actor_name || log.actor_email || 'System';
const normalizeMultiSelectValue = (value) => (Array.isArray(value) ? value : [value]);

const ActivityTab = ({
  technicianId,
  refreshToken = 0,
  onActivitiesCountChange,
}) => {
  const theme = useTheme();
  const filterButtonColor = theme.palette.mode === 'dark' ? '#fff' : '#000';
  const toolbarButtonSx = {
    textTransform: 'none',
    minHeight: 40,
    borderRadius: 1,
    borderColor: theme.palette.divider,
    color: filterButtonColor,
    px: 1.5,
    '&:hover': {
      borderColor: theme.palette.text.primary,
      backgroundColor: theme.palette.action.hover,
    },
  };

  const [logs, setLogs] = useState([]);
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [error, setError] = useState('');
  const [sortOrder, setSortOrder] = useState('desc');
  const [activityTypeFilter, setActivityTypeFilter] = useState([]);
  const [actorFilter, setActorFilter] = useState([]);
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  const activityTypeOptions = useMemo(() => (
    Object.entries(ACTIVITY_TYPE_CONFIG)
      .filter(([key]) => key !== 'other')
      .map(([key, config]) => ({ value: key, label: config.label }))
  ), []);

  const actorOptions = useMemo(() => {
    const optionsMap = new Map();
    logs.forEach((log) => {
      const value = getActorFilterValue(log);
      if (!optionsMap.has(value)) {
        optionsMap.set(value, getActorFilterLabel(log));
      }
    });

    return Array.from(optionsMap.entries())
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [logs]);

  const filteredAndSortedLogs = useMemo(() => {
    const fromTimestamp = toStartOfDay(fromDate);
    const toTimestamp = toEndOfDay(toDate);

    const filteredLogs = logs.filter((log) => {
      const logType = getActivityType(log);
      if (activityTypeFilter.length > 0 && !activityTypeFilter.includes(logType)) {
        return false;
      }

      const logActor = getActorFilterValue(log);
      if (actorFilter.length > 0 && !actorFilter.includes(logActor)) {
        return false;
      }

      const logTimestamp = new Date(log.created_at).getTime();
      if (!Number.isNaN(logTimestamp)) {
        if (fromTimestamp !== null && logTimestamp < fromTimestamp) return false;
        if (toTimestamp !== null && logTimestamp > toTimestamp) return false;
      }

      return true;
    });

    return filteredLogs.sort((a, b) => {
      const first = new Date(a.created_at).getTime();
      const second = new Date(b.created_at).getTime();
      return sortOrder === 'desc' ? second - first : first - second;
    });
  }, [logs, sortOrder, activityTypeFilter, actorFilter, fromDate, toDate]);

  useEffect(() => {
    let cancelled = false;

    const fetchLogs = async () => {
      if (!technicianId) {
        setLogs([]);
        if (onActivitiesCountChange) onActivitiesCountChange(0);
        return;
      }
      setLogs([]);
      if (onActivitiesCountChange) onActivitiesCountChange(0);
      setLoadingLogs(true);
      setError('');
      const { data, status, message } = await GET(api.getTechnicianLogs(technicianId));
      if (cancelled) return;
      if (status === 200) {
        const nextLogs = Array.isArray(data) ? data : [];
        setLogs(nextLogs);
        if (onActivitiesCountChange) onActivitiesCountChange(nextLogs.length);
      } else {
        setError(message || 'Failed to load activity.');
      }
      setLoadingLogs(false);
    };

    fetchLogs();
    return () => {
      cancelled = true;
    };
  }, [technicianId, refreshToken]);

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      {loadingLogs && logs.length === 0 ? (
        <Box sx={{ p: 0 }}>
          <Skeleton variant="text" width={220} height={30} />
          <Skeleton variant="rectangular" sx={{ mt: 1, borderRadius: 1 }} height={90} />
          <Skeleton variant="rectangular" sx={{ mt: 1, borderRadius: 1 }} height={90} />
        </Box>
      ) : error ? (
        <Alert severity="error">{error}</Alert>
      ) : (
        <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={1}
            alignItems={{ xs: 'stretch', md: 'center' }}
            justifyContent="flex-start"
            useFlexGap
            sx={{ mb: 2, flexWrap: 'wrap' }}
          >
            <Button
              size="small"
              variant="outlined"
              onClick={() => setSortOrder((current) => (current === 'desc' ? 'asc' : 'desc'))}
              startIcon={<SwapVertOutlinedIcon fontSize="small" />}
              sx={{ ...toolbarButtonSx, minWidth: 148 }}
            >
              {sortOrder === 'desc' ? 'Newest first' : 'Oldest first'}
            </Button>

            <TextField
              size="small"
              select
              label="Activity type"
              value={activityTypeFilter}
              onChange={(event) => setActivityTypeFilter(normalizeMultiSelectValue(event.target.value))}
              SelectProps={{
                multiple: true,
                renderValue: (selected) => {
                  if (!selected?.length) return 'All activity';
                  return selected
                    .map((value) => activityTypeOptions.find((option) => option.value === value)?.label || value)
                    .join(', ');
                },
              }}
              sx={{ minWidth: 180 }}
            >
              {activityTypeOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  <Checkbox size="small" checked={activityTypeFilter.includes(option.value)} />
                  <ListItemText primary={option.label} />
                </MenuItem>
              ))}
            </TextField>

            <TextField
              size="small"
              select
              label="User"
              value={actorFilter}
              onChange={(event) => setActorFilter(normalizeMultiSelectValue(event.target.value))}
              SelectProps={{
                multiple: true,
                renderValue: (selected) => {
                  if (!selected?.length) return 'All users';
                  return selected
                    .map((value) => actorOptions.find((option) => option.value === value)?.label || value)
                    .join(', ');
                },
              }}
              sx={{ minWidth: 180 }}
            >
              {actorOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  <Checkbox size="small" checked={actorFilter.includes(option.value)} />
                  <ListItemText primary={option.label} />
                </MenuItem>
              ))}
            </TextField>

            <TextField
              size="small"
              label="From"
              type="date"
              value={fromDate}
              onChange={(event) => setFromDate(event.target.value)}
              InputLabelProps={{ shrink: true }}
            />

            <TextField
              size="small"
              label="To"
              type="date"
              value={toDate}
              onChange={(event) => setToDate(event.target.value)}
              InputLabelProps={{ shrink: true }}
            />

            <Button
              size="small"
              variant="outlined"
              onClick={() => {
                setActivityTypeFilter([]);
                setActorFilter([]);
                setFromDate('');
                setToDate('');
              }}
              sx={{
                ...toolbarButtonSx,
                minWidth: 120,
                alignSelf: { xs: 'flex-start', md: 'center' },
              }}
            >
              Clear filters
            </Button>
          </Stack>

          {filteredAndSortedLogs.length === 0 ? (
            <Paper
              variant="outlined"
              sx={{
                p: 3,
                borderRadius: 2,
                textAlign: 'center',
              }}
            >
              <Typography sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                {logs.length === 0
                  ? 'No activity recorded for this technician yet.'
                  : 'No activity matches the selected filters.'}
              </Typography>
            </Paper>
          ) : (
            filteredAndSortedLogs.map((log) => {
              const activityType = getActivityType(log);
              const activityConfig = ACTIVITY_TYPE_CONFIG[activityType] || ACTIVITY_TYPE_CONFIG.other;
              const ActivityIcon = activityConfig.icon;

              return (
                <Paper
                  key={log.id}
                  elevation={0}
                  variant="outlined"
                  sx={{
                    mb: 1.5,
                    p: 1.5,
                    borderRadius: 2,
                    borderColor: theme.palette.divider,
                    backgroundColor: theme.palette.background.paper,
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      borderColor: theme.palette.action.focus,
                      boxShadow: theme.shadows[2],
                    },
                  }}
                >
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    spacing={1}
                    sx={{ mb: 1 }}
                  >
                    <Chip
                      icon={<ActivityIcon />}
                      size="small"
                      label={activityConfig.label}
                      color={activityConfig.color}
                      variant={theme.palette.mode === 'dark' ? 'filled' : 'outlined'}
                    />
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      {formatShortDateTime(log.created_at)}
                    </Typography>
                  </Stack>

                  <Typography variant="body2" sx={{ fontWeight: 600, whiteSpace: 'pre-line' }}>
                    {formatTechnicianLogEntry(log)}
                  </Typography>

                  <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.75 }}>
                    By {log.actor_name || log.actor_email || 'System'}
                  </Typography>
                </Paper>
              );
            })
          )}

          {loadingLogs && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={20} />
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

export default ActivityTab;
